import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InfoOutlined, LockOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, FormControl, FormHelperText, FormLabel, IconButton, InputAdornment, OutlinedInput, Stack, styled } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import Big from "big.js";
import { forwardRef, useId, useState } from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat, PatternFormat } from "react-number-format";
import { formatCurrency, formatDate, formatPercent } from "../formatters";
import { dayjsDate, isValidDate } from "../utils";
import { IconTooltip } from "./IconTooltip";
const StyledOutlinedInput = styled(OutlinedInput, {
    shouldForwardProp: (propName)=>![
            'textAlign'
        ].includes(propName)
})(({ textAlign })=>({
        '.MuiInputBase-input': {
            textAlign
        }
    }));
const Formatter = /*#__PURE__*/ forwardRef(function Formatter(props, ref) {
    const { decimalScale, onChange, patternFormat, patternPlaceholder, useUtc, ...other } = props;
    const format = props['data-format'];
    const onValueChange = (values)=>{
        if (!values) {
            return;
        }
        if (format === 'date' && !dayjsDate(values.formattedValue).isValid()) {
            onChange({
                target: {
                    name: props.name,
                    value: values.formattedValue
                }
            });
            return;
        }
        if (format === 'date') {
            const parsed = dayjsDate(values.formattedValue);
            const isoValue = new Date(Date.UTC(parsed.year(), parsed.month(), parsed.date(), 12, 0, 0, 0)).toISOString();
            if (isoValue === props.value) {
                return;
            }
            onChange({
                target: {
                    name: props.name,
                    value: isoValue
                }
            });
            return;
        }
        const value = format === 'phone' ? values.value ? `+1${values.value}` : '' : format === 'percent' ? values.floatValue == null ? null : Big(values.floatValue).div(100).toNumber() : [
            'accountNumber',
            'routingNumber',
            'ssn',
            'zip'
        ].includes(format) ? values.value : values.floatValue ?? null;
        onChange({
            target: {
                name: props.name,
                value
            }
        });
    };
    if (format === 'phone') {
        return /*#__PURE__*/ _jsx(PatternFormat, {
            ...other,
            format: "(###) ###-####",
            getInputRef: ref,
            mask: "_",
            onValueChange: onValueChange,
            value: other.value?.replace('+1', '') ?? ''
        });
    }
    if (format === 'ssn') {
        return /*#__PURE__*/ _jsx(PatternFormat, {
            ...other,
            format: "###-##-####",
            getInputRef: ref,
            mask: "_",
            onValueChange: onValueChange
        });
    }
    if (format === 'date') {
        const displayValue = isValidDate(other.value) ? formatDate(other.value, {
            format: patternPlaceholder ?? 'MM/DD/YYYY',
            useUtc
        }) : other.value;
        return /*#__PURE__*/ _jsx(PatternFormat, {
            ...other,
            format: patternFormat ?? '##/##/####',
            getInputRef: ref,
            mask: "_",
            onValueChange: onValueChange,
            placeholder: patternPlaceholder ?? 'MM/DD/YYYY',
            value: displayValue
        });
    }
    if (format === 'currency') {
        return /*#__PURE__*/ _jsx(NumericFormat, {
            ...other,
            allowNegative: props.min < 0,
            decimalScale: decimalScale ?? 2,
            fixedDecimalScale: true,
            getInputRef: ref,
            onValueChange: onValueChange,
            prefix: "$",
            thousandSeparator: ","
        });
    }
    if (format === 'percent') {
        return /*#__PURE__*/ _jsx(NumericFormat, {
            ...other,
            allowNegative: props.min < 0,
            decimalScale: decimalScale ?? 0,
            fixedDecimalScale: true,
            getInputRef: ref,
            onValueChange: onValueChange,
            suffix: "%",
            value: Big(other.value ?? 0).mul(100).toNumber()
        });
    }
    if (format === 'number') {
        return /*#__PURE__*/ _jsx(NumericFormat, {
            ...other,
            allowNegative: props.min < 0,
            decimalScale: decimalScale ?? 0,
            fixedDecimalScale: true,
            getInputRef: ref,
            onValueChange: onValueChange,
            value: other.value ?? ''
        });
    }
    if (format === 'accountNumber') {
        return /*#__PURE__*/ _jsx(PatternFormat, {
            ...other,
            format: "#################",
            getInputRef: ref,
            mask: "_",
            onValueChange: onValueChange
        });
    }
    if (format === 'routingNumber') {
        return /*#__PURE__*/ _jsx(PatternFormat, {
            ...other,
            format: "#########",
            getInputRef: ref,
            mask: "_",
            onValueChange: onValueChange
        });
    }
    if (format === 'zip') {
        return /*#__PURE__*/ _jsx(PatternFormat, {
            ...other,
            format: "#########",
            getInputRef: ref,
            mask: "_",
            onValueChange: onValueChange
        });
    }
});
const formatMinMaxValue = (value, format)=>{
    if (value === undefined || !Number.isFinite(value)) {
        return;
    }
    if (format === 'currency') {
        return formatCurrency(value);
    }
    if (format === 'percent') {
        return formatPercent(value);
    }
    return value;
};
export const Input = (props)=>{
    const labelId = useId();
    const errorId = useId();
    const labelInfoId = useId();
    const [isValueVisible, setValueVisible] = useState(false);
    const formContext = useFormContext();
    const translation = useTranslation();
    const isPassword = props.format === 'password';
    const isSsn = props.format === 'ssn';
    const isMaskable = isPassword || isSsn && !!props.maskable;
    const isMasked = isMaskable && !isValueVisible;
    /* applies regardless of isMasked — the mask only swaps the native input's
     type to 'password' for visual obscuring, it must not also disable the
     underlying format/length restriction while hidden */ const isFormatter = [
        'accountNumber',
        'currency',
        'date',
        'number',
        'percent',
        'phone',
        'routingNumber',
        'ssn',
        'zip'
    ].includes(props.format);
    const isUrl = props.format === 'url';
    const stripUrl = (value)=>typeof value === 'string' ? value.replace(/^https?:\/\//, '') : '';
    const buildUrl = (raw)=>{
        const stripped = stripUrl(raw);
        return stripped ? `https://${stripped}` : '';
    };
    const error = get(formContext.formState.errors, props.name);
    const onChange = (field, value)=>{
        field.onChange(value);
        props.onChange?.(value);
    };
    const onToggleValueVisible = ()=>{
        setValueVisible((prev)=>!prev);
    };
    const maskLabel = isSsn ? 'SSN' : 'password';
    const maskEndAdornment = isMaskable ? /*#__PURE__*/ _jsx(InputAdornment, {
        position: "end",
        children: /*#__PURE__*/ _jsx(IconButton, {
            "aria-label": `${isValueVisible ? 'Hide' : 'Show'} ${maskLabel}`,
            "data-testid": `${props['data-testid'] ?? props.name}Toggle`,
            edge: "end",
            onClick: onToggleValueVisible,
            size: "small",
            children: isValueVisible ? /*#__PURE__*/ _jsx(VisibilityOff, {}) : /*#__PURE__*/ _jsx(Visibility, {})
        })
    }) : props.endAdornment;
    return /*#__PURE__*/ _jsx(Controller, {
        control: formContext.control,
        name: props.name,
        render: ({ field })=>/*#__PURE__*/ _jsxs(FormControl, {
                error: !!error,
                fullWidth: props.fullWidth,
                required: props.required,
                size: props.size,
                children: [
                    /*#__PURE__*/ _jsxs(Stack, {
                        alignItems: "center",
                        direction: "row",
                        spacing: 0.5,
                        children: [
                            /*#__PURE__*/ _jsx(FormLabel, {
                                disabled: props.disabled,
                                id: labelId,
                                required: props.required,
                                sx: props.hideLabel ? visuallyHidden : undefined,
                                children: props.label
                            }),
                            props.labelInfo && /*#__PURE__*/ _jsx(IconTooltip, {
                                "aria-label": `${props.label} more info`,
                                trigger: /*#__PURE__*/ _jsx(InfoOutlined, {
                                    color: "primary",
                                    fontSize: "small"
                                }),
                                children: props.labelInfo
                            })
                        ]
                    }),
                    props.labelInfo && /*#__PURE__*/ _jsx(Box, {
                        component: "span",
                        id: labelInfoId,
                        sx: visuallyHidden,
                        children: props.labelInfo
                    }),
                    /*#__PURE__*/ _jsx(StyledOutlinedInput, {
                        ...field,
                        autoComplete: props.autoComplete,
                        defaultValue: props.defaultValue,
                        disabled: props.disabled,
                        endAdornment: maskEndAdornment,
                        error: !!error,
                        fullWidth: props.fullWidth,
                        inputComponent: isFormatter ? Formatter : undefined,
                        inputProps: {
                            'aria-describedby': props.labelInfo ? `${labelInfoId} ${errorId}` : errorId,
                            'aria-disabled': !!props.disabled,
                            'aria-labelledby': labelId,
                            'aria-live': 'polite',
                            'aria-required': !!props.disabled,
                            'aria-valuemax': props.max,
                            'aria-valuemin': props.min,
                            'aria-valuenow': field.value,
                            ...props['data-1p-ignore'] && {
                                'data-1p-ignore': true
                            },
                            'data-component': 'input',
                            'data-format': props.format,
                            'data-testid': props['data-testid'] ?? props.name,
                            max: props.max,
                            min: props.min,
                            ...isFormatter ? {
                                decimalScale: props.decimalScale,
                                patternFormat: props.patternFormat,
                                patternPlaceholder: props.patternPlaceholder,
                                useUtc: props.useUtc
                            } : {}
                        },
                        maxRows: props.maxRows,
                        multiline: props.multiline,
                        onChange: (e)=>onChange(field, isUrl ? buildUrl(e.target.value) : e.target.value),
                        placeholder: props.placeholder,
                        readOnly: props.readOnly,
                        required: props.required,
                        rows: props.rows,
                        size: props.size,
                        startAdornment: isUrl ? /*#__PURE__*/ _jsx(InputAdornment, {
                            position: "start",
                            children: "https://"
                        }) : props.disabled ? /*#__PURE__*/ _jsx(InputAdornment, {
                            position: "start",
                            children: props.startAdornmentTooltip ? /*#__PURE__*/ _jsx(IconTooltip, {
                                size: "medium",
                                trigger: /*#__PURE__*/ _jsx(LockOutlined, {}),
                                children: props.startAdornmentTooltip
                            }) : /*#__PURE__*/ _jsx(LockOutlined, {})
                        }) : props.startAdornmentIcon ? /*#__PURE__*/ _jsx(InputAdornment, {
                            position: "start",
                            children: props.startAdornmentTooltip ? /*#__PURE__*/ _jsx(IconTooltip, {
                                size: "medium",
                                trigger: props.startAdornmentIcon,
                                children: props.startAdornmentTooltip
                            }) : props.startAdornmentIcon
                        }) : props.startAdornment,
                        sx: props.sx,
                        textAlign: props.textAlign,
                        type: isMasked ? 'password' : isMaskable ? 'text' : props.type,
                        value: isUrl ? stripUrl(field.value) : field.value
                    }),
                    !props.hideError && /*#__PURE__*/ _jsx(FormHelperText, {
                        "aria-atomic": "true",
                        "data-component": "inputError",
                        "data-testid": props['data-testid'] ?? props.name,
                        id: errorId,
                        children: translation.t(error?.message, {
                            max: formatMinMaxValue(props.max, props.format),
                            min: formatMinMaxValue(props.min, props.format)
                        }) || props.helperText || ' '
                    })
                ]
            })
    });
};
Input.displayName = 'Input';

//# sourceMappingURL=Input.js.map